import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', url: '' });

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // Récupérer les détails du paiement avec les informations de l'utilisateur
        const { data, error } = await supabase
          .from('payments')
          .select(`
            id,
            transaction_id,
            amount,
            currency,
            payment_status,
            payment_method,
            created_at,
            updated_at,
            users:users(id, firstname, lastname, email, phone),
            shipments:shipments(tracking_number, recipient_name, recipient_address, carrier, status)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;

        // Formater les données pour le composant
        const formattedPayment = {
          ...data,
          user: {
            id: data.users.id,
            name: `${data.users.firstname} ${data.users.lastname}`,
            email: data.users.email,
            phone: data.users.phone
          },
          shipment: data.shipments && data.shipments.length > 0 ? {
            tracking_number: data.shipments[0].tracking_number,
            recipient_name: data.shipments[0].recipient_name,
            recipient_address: data.shipments[0].recipient_address,
            carrier: data.shipments[0].carrier,
            status: data.shipments[0].status
          } : null
        };

        // Récupérer les documents liés au paiement
        const { data: documentsData } = await supabase
          .from('consular_acts')
          .select('id, title, description, processing_fee, shipping_fee')
          .eq('id', id);

        formattedPayment.documents = documentsData || [];

        setPayment(formattedPayment);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des détails du paiement :', err);
        setError(err);
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [id]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSignedUrl = async (bucket, filename) => {
    try {
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(filename, 300); // URL valide pendant 5 minutes

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'URL signée pour ${filename}:`, error);
      throw error;
    }
  };

  const handleViewDocument = async (documentType) => {
    try {
      let url, title, filename;
      
      if (documentType === 'receipt') {
        filename = `${payment.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        title = 'Reçu de paiement';
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${payment.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        title = 'Formulaire d\'expédition';
      }

      setModalContent({ title, url });
      setModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de l\'affichage du document:', error);
      toast.error(`Erreur lors de l'affichage du document. Le fichier n'existe peut-être pas.`);
    }
  };

  const handleDownloadDocument = async (documentType) => {
    try {
      let url, filename, downloadName;
      
      if (documentType === 'receipt') {
        filename = `${payment.transaction_id}.pdf`;
        url = await getSignedUrl('receipt', filename);
        downloadName = `Reçu_${payment.transaction_id}.pdf`;
      } else if (documentType === 'shipping') {
        filename = `Formulaire_${payment.transaction_id}.pdf`;
        url = await getSignedUrl('formulaires', filename);
        downloadName = filename;
      }

      // Télécharger le fichier
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Créer un lien temporaire pour le téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      toast.error(`Erreur lors du téléchargement du document. Le fichier n'existe peut-être pas.`);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        Erreur lors du chargement du paiement : {error.message}
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        Paiement non trouvé
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Détails du paiement #{payment.transaction_id}</h2>
        <button
          onClick={() => navigate('/admin/payments')}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Retour à la liste
        </button>
      </div>
      
      {/* Modal pour afficher les PDF */}
      <Transition show={modalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {modalContent.title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2 bg-gray-100 rounded-lg p-1 h-[70vh]">
                  <iframe
                    src={modalContent.url}
                    className="w-full h-full rounded"
                    title={modalContent.title}
                  />
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={closeModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Document Buttons */}
        <div className="bg-white shadow-md rounded-lg p-6 md:col-span-2">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Reçu de paiement */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-lg mb-3">Reçu de paiement</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleViewDocument('receipt')}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Voir le document
                </button>
                <button
                  onClick={() => handleDownloadDocument('receipt')}
                  className="inline-flex items-center px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger
                </button>
              </div>
            </div>
            
            {/* Formulaire d'expédition */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-lg mb-3">Formulaire d&apos;expédition</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleViewDocument('shipping')}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Voir le document
                </button>
                <button
                  onClick={() => handleDownloadDocument('shipping')}
                  className="inline-flex items-center px-3 py-2 bg-secondary text-white rounded hover:bg-secondary/90 transition-colors text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Payment Information */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Informations de paiement</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">ID de transaction:</span>
              <span className="font-medium">{payment.transaction_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Montant:</span>
              <span className="font-medium">{payment.amount.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Statut:</span>
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.payment_status)}`}>
                {payment.payment_status === 'succeeded' ? 'Complété' : payment.payment_status === 'pending' ? 'En attente' : 'Échoué'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Méthode de paiement:</span>
              <span className="font-medium">{payment.payment_method === 'card' ? 'Carte bancaire' : payment.payment_method}</span>
            </div>
            {payment.card_info && (
              <div className="flex justify-between">
                <span className="text-gray-600">Carte:</span>
                <span className="font-medium">{payment.card_info.brand} **** {payment.card_info.last4}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Date de création:</span>
              <span className="font-medium">{new Date(payment.created_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Dernière mise à jour:</span>
              <span className="font-medium">{new Date(payment.updated_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        {/* User Information */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Informations de l&apos;utilisateur</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">ID:</span>
              <span className="font-medium">{payment.user.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Nom:</span>
              <span className="font-medium">{payment.user.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium">{payment.user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Téléphone:</span>
              <span className="font-medium">{payment.user.phone}</span>
            </div>
          </div>
        </div>
        
        {/* Shipment Information */}
        {payment.shipment && (
          <div className="bg-white shadow-md rounded-lg p-6 md:col-span-2">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Informations d&apos;expédition</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Numéro de suivi:</span>
                <span className="font-medium">
                  {payment.shipment.tracking_number && payment.shipment.tracking_number.startsWith('CD')
                    ? 'En attente'
                    : payment.shipment.tracking_number || 'Non spécifié'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Destinataire:</span>
                <span className="font-medium">{payment.shipment.recipient_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Adresse:</span>
                <span className="font-medium">{payment.shipment.recipient_address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transporteur:</span>
                <span className="font-medium">{payment.shipment.carrier}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Statut:</span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.shipment.status)}`}>
                  {payment.shipment.status === 'completed' ? 'Livré' : payment.shipment.status === 'pending' ? 'En cours' : 'Problème'}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Documents */}
        {payment.documents && payment.documents.length > 0 && (
          <div className="bg-white shadow-md rounded-lg p-6 md:col-span-2">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Actes consulaires</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Titre
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frais de traitement
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frais d&apos;expédition
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payment.documents.map((doc) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{doc.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.processing_fee ? doc.processing_fee.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.shipping_fee ? doc.shipping_fee.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentDetails;

